import { Branch, ResultGetSupplierConfiguration } from '../../api/dealer-api-interface-franchisee';
import { branchUsersCache } from '../cache-impl/branch-users-cache';
import { customElement, property } from 'lit/decorators.js';
import { DateRange } from '../dashboard/widget/dashboard-filter';
import { emptyGuid, newGuid } from '../../../webmodule-common/other/api/guid';
import { dashboard, flushDashboard } from '../dashboard/data/dashboard-cache';
import { getApiFactory } from '../../api/api-injector';
import { getInternalId } from '../../../webmodule-common/other/ui/databinding/databinding';
import { getSupplier } from '../quotes/supplier-selector';
import { html, PropertyValues, TemplateResult } from 'lit';
import { PageBase } from './helpers/page-base';
import { TabManager } from '../../../webmodule-common/other/ui/tab-manager/tab-manager';
import type { TabPage } from '../../../webmodule-common/other/ui/tab-manager/tab-manager-types';
import { QuoteState } from '../../api/dealer-api-interface-quote';
import { LangAppHome } from '../../../localization/scripts/dealer-franchisee/pages/app-home-lang';
import { LangDashboardFilter } from '../../../localization/scripts/dealer-franchisee/dashboard/widget/dashboard-filter-lang';
import { userDataStore } from '../common/current-user-data-store';
import { getSupplierConfigurationOrLastKnown } from '../../api/supplier-api-implementation';

/**
 * configure the dashboard based on the type of user.
 * we will determine this user level by using a security level on the backend
 * and converting that to a type
 * or maybe add a table that the manager can allocate and configure
 */
const dashboardContentType = {
  unknown: 0,
  /**
   * this is the typical quote creator, and only one implemented
   */
  branchUser: 1,
  franchiseManager: 2,
  franchiseAdministrator: 4
};

@customElement('app-home')
export class AppHome extends PageBase {
  internalId: string = getInternalId();
  // For more information on using properties and state in lit
  // check out this link https://lit.dev/docs/components/properties/
  @property() message = LangAppHome.welcome();
  @property({ type: Number }) contentType = 0;

  private currentDate = new Date();

  private availableDateRanges: DateRange[] = [];
  @property({ type: Object }) private selectedDateRange?: DateRange;

  private availableBranches: Branch[] = [];
  @property() private selectedBranchId?: string;
  @property() private selectedUserId?: string;
  @property() private selectedBranchIdForPipeline?: string;
  @property() private selectedUserIdForPipeline?: string;
  @property({ type: Object }) private selectedDateRangeForPipeline?: DateRange;

  private userCache = branchUsersCache();

  @property({ type: Array }) private quoteStates: QuoteState[] = [];

  private tabManager!: TabManager;

  constructor() {
    super();
    this.contentType = dashboardContentType.branchUser;
    this.tabManager = this.createTabManager();
  }

  private get elementId(): string {
    return `dashboard-page-${this.internalId}`;
  }

  protected async afterUserConnected() {
    this.requestUpdate();
  }

  protected async awaken() {
    //if we ever reach this page and we are not logged in
    //then we want to force a page reload which will
    //take us through our full reload process of the apoplication
    //in index.html including the user login process
    if (!this.loggedIn) setTimeout(() => (location.href = ''), 100);

    const last6months = new Date(this.currentDate);
    last6months.setMonth(last6months.getMonth() - 6);
    this.addDateRange(LangAppHome.last6Months(), last6months);

    const last90days = new Date(this.currentDate);
    last90days.setMonth(last90days.getMonth() - 3);
    this.addDateRange(LangAppHome.last90Days(), last90days);

    const last60days = new Date(this.currentDate);
    last60days.setMonth(last60days.getMonth() - 2);
    this.addDateRange(LangAppHome.last60Days(), last60days);

    // get start date for activity over the past month
    const last30days = new Date(this.currentDate);
    last30days.setMonth(last30days.getMonth() - 1);
    this.addDateRange(LangAppHome.last30Days(), last30days);

    const last7days = new Date(this.currentDate);
    last7days.setDate(last7days.getDate() - 7);
    this.addDateRange(LangAppHome.last7Days(), last7days);

    const last24hours = new Date(this.currentDate);
    last24hours.setDate(last24hours.getDate() - 1);
    this.addDateRange(LangAppHome.last24Hours(), last24hours);

    await userDataStore.reLoadFranchisee();
    const branches = userDataStore.getUserBranches();
    this.availableBranches = branches;

    // No usable branches: skip branch-scoped dashboard APIs.
    if (!branches.length) return;

    await this.userCache.prefetch(branches.map(b => b.id));

    // set properties below here - ensures that all widgets update at once
    this.quoteStates = await this.getQuoteStates();
    this.selectedDateRange = this.availableDateRanges[3];
    this.selectedDateRangeForPipeline = this.availableDateRanges[3]; // Same default as overview
    this.selectedBranchIdForPipeline = branches && branches.length === 1 ? branches[0].id : emptyGuid;
    this.selectedUserIdForPipeline = emptyGuid;
    if (branches && branches.length === 1) {
      this.selectedBranchId = branches[0].id;
    }
    dashboard().resetData(
      this.availableBranches.map(x => x.id),
      this.selectedBranchId
    );
  }

  protected async getQuoteStates(): Promise<QuoteState[]> {
    const quoteStates = [
      QuoteState.Draft,
      QuoteState.Active,
      QuoteState.IssuePending,
      QuoteState.Issued,
      QuoteState.Accepted
    ];

    const api = getApiFactory().supplier();
    const supplierId = (await getSupplier()).supplierId;
    const cachedConfig: ResultGetSupplierConfiguration | null = await getSupplierConfigurationOrLastKnown(
      api,
      supplierId
    );

    // Exclude supplier review states from dashboard totals in fabricator mode
    if (!userDataStore.isFabricatorMode && cachedConfig && cachedConfig.allowSupplierReview) {
      quoteStates.push(
        QuoteState.Accepted_AssignedToReviewer,
        QuoteState.SupplierReviewPending,
        QuoteState.SupplierReviewed
      );
    }
    quoteStates.push(QuoteState.Approved);
    return quoteStates;
  }

  render() {
    if (!this.loggedIn) return html``;
    return html`
      <div id=${this.elementId} class="page-content">
        <div class="page-container">${this.getTemplateForCurrentUser()}</div>
      </div>
    `;
  }

  updated(changedProperties: PropertyValues<this>) {
    super.updated(changedProperties);
    // The tab panels are rendered by the tab manager (a separate element), so a
    // filter change on this page will not reach the filtered widgets on its own.
    // Only re-render the manager when a widget input actually changed: forwarding
    // on *every* update (e.g. child `ui-changed` events bubbling up) churns the
    // widgets' data fetch and can leave a chart stuck on "Loading...".
    const widgetInputs = [
      'selectedDateRange',
      'selectedBranchId',
      'selectedUserId',
      'quoteStates',
      'contentType',
      'selectedDateRangeForPipeline',
      'selectedBranchIdForPipeline',
      'selectedUserIdForPipeline'
    ];
    if (widgetInputs.some(p => changedProperties.has(p as keyof AppHome))) {
      this.tabManager?.requestUpdate();
    }
  }

  getTemplateForCurrentUser(): TemplateResult {
    if (this.contentType === dashboardContentType.branchUser) return this.getTemplateForBranchUser();
    return html``;
  }

  getTemplateForBranchUser(): TemplateResult {
    return html`${this.tabManager.ui}`;
  }

  private createTabManager(): TabManager {
    const pages: TabPage[] = [
      {
        caption: () => LangAppHome.overviewTab(),
        fragment: 'overview',
        content: () => this.renderOverviewTab()
      },
      {
        caption: () => LangAppHome.deliveryPipelineTab(),
        fragment: 'delivery-pipeline',
        content: () => this.renderDeliveryPipelineTab()
      }
    ];
    return new TabManager({ initialIndex: 0, pages });
  }

  private renderOverviewTab(): TemplateResult {
    // Defer creating the data widgets until the initial filter inputs are ready.
    // On the empty first pass (before awaken sets the date range / quote states)
    // the apex chart widgets would set up, then immediately re-render at a new
    // width once the inputs arrive - a churn their async chart setup can lose,
    // leaving a chart stuck on its "Loading..." placeholder. Creating them once,
    // with valid inputs and at the final width, renders cleanly.
    const inputsReady = !!this.selectedDateRange && this.quoteStates.length > 0;
    return html` <div class="dashboard-tab dashboard-tab-overview">
      <wm-dashboard-filter
        class="dashboard-item-filter"
        .dates=${this.availableDateRanges}
        .branches=${this.availableBranches}
        .users=${this.userCache.getUsersForBranch(this.selectedBranchId)}
        .selectedDate=${this.selectedDateRange}
        .selectedBranchId=${this.selectedBranchId}
        .selectedUserId=${this.selectedUserId}
        @wm-event-date-changed=${this.onDateRangeChanged}
        @wm-event-branch-changed=${this.onBranchChanged}
        @wm-event-user-changed=${this.onUserChanged}
        @wm-event-reset-filters=${this.onFilterReset}
      ></wm-dashboard-filter>
      ${inputsReady
        ? html` <div class="dashboard-overview-body">
            <div class="dashboard-items-wrapper dashboard-kpi-items">
              <div class="dashboard-item dashboard-item-piechart">
                <sales-success-widget
                  .dateRange=${this.selectedDateRange}
                  .branchId=${this.selectedBranchId}
                  .userId=${this.selectedUserId}
                ></sales-success-widget>
              </div>
              <div class="dashboard-item dashboard-item-piechart">
                <dashboard-branch-monthly-revenue-pipeline
                  .dashboardCache=${dashboard()}
                  .assignedToUserId=${this.selectedUserId}
                ></dashboard-branch-monthly-revenue-pipeline>
              </div>
            </div>
            <div class="dashboard-items-wrapper dashboard-chart-items">
              <div class="dashboard-item dashboard-item-barchart">
                <issued-quotes-revenue-widget
                  .dateRange=${this.selectedDateRange}
                  .dashboardCache=${dashboard()}
                  .branchId=${this.selectedBranchId}
                  .userId=${this.selectedUserId}
                  .states=${this.quoteStates}
                ></issued-quotes-revenue-widget>
              </div>
              <div class="dashboard-item dashboard-item-barchart">
                <generated-purchase-orders-revenue-widget
                  .dateRange=${this.selectedDateRange}
                  .dashboardCache=${dashboard()}
                  .branchId=${this.selectedBranchId}
                  .userId=${this.selectedUserId}
                ></generated-purchase-orders-revenue-widget>
              </div>
            </div>
          </div>`
        : html``}
    </div>`;
  }

  private renderDeliveryPipelineTab(): TemplateResult {
    const inputsReady = !!this.selectedDateRangeForPipeline;

    return html` <div class="dashboard-tab dashboard-tab-overview">
      <wm-dashboard-filter
        class="dashboard-item-filter"
        .dates=${this.availableDateRanges}
        .branches=${this.availableBranches}
        .users=${this.userCache.getUsersForBranch(this.selectedBranchIdForPipeline)}
        .selectedDate=${this.selectedDateRangeForPipeline}
        .selectedBranchId=${this.selectedBranchIdForPipeline}
        .selectedUserId=${this.selectedUserIdForPipeline}
        .filterTooltip=${LangDashboardFilter.filterTooltip()}
        @wm-event-date-changed=${this.onDateRangeChangedForPipeline}
        @wm-event-branch-changed=${this.onBranchChangedForPipeline}
        @wm-event-user-changed=${this.onUserChangedForPipeline}
        @wm-event-reset-filters=${this.onFilterResetForPipeline}
      ></wm-dashboard-filter>
      ${inputsReady
        ? html` <div class="dashboard-tab dashboard-tab-delivery">
            <dashboard-branch-monthly-delivery-pipeline
              .dateRange=${this.selectedDateRangeForPipeline}
              .branchId=${this.selectedBranchIdForPipeline}
              .assignedToUserId=${this.selectedUserIdForPipeline}
            ></dashboard-branch-monthly-delivery-pipeline>
          </div>`
        : html``}
    </div>`;
  }

  async dispose() {
    flushDashboard();
  }

  meta() {
    return {
      title: `Dealer's Module`,
      titleTemplate: null,
      description: 'SoftTech dealer CPQ'
    };
  }

  protected addDateRange(label: string, startDate: Date) {
    this.availableDateRanges.push({
      id: newGuid(),
      label: label,
      startDate: startDate,
      endDate: this.currentDate
    });
  }

  protected onDateRangeChanged = (e: CustomEvent<{ date?: DateRange }>) => {
    const dateRange = e.detail.date;
    if (dateRange) {
      this.selectedDateRange = dateRange;
    }
  };

  protected onBranchChanged = (e: CustomEvent<{ branchId?: string }>) => {
    const branchId = e.detail.branchId;
    if (branchId) {
      this.selectedBranchId = branchId;
      this.selectedUserId = emptyGuid;
      dashboard().resetData(
        this.availableBranches.map(x => x.id),
        this.selectedBranchId
      );
    }
  };

  protected onUserChanged = (e: CustomEvent<{ userId?: string }>) => {
    if (e.detail.userId) {
      this.selectedUserId = e.detail.userId;
    }
  };

  protected onFilterReset = (_: CustomEvent<NonNullable<unknown>>) => {
    if (this.availableDateRanges.length > 1) this.selectedDateRange = this.availableDateRanges[1];
    // Mirror the initial-load default: a user with a single branch has no "all
    // branches" option, so reset back to that branch rather than emptyGuid (which
    // the caches send to the backend as the all-branches sentinel).
    this.selectedBranchId = this.availableBranches.length === 1 ? this.availableBranches[0].id : emptyGuid;
    this.selectedUserId = emptyGuid;
  };
  protected onDateRangeChangedForPipeline = (e: CustomEvent<{ date?: DateRange }>) => {
    const dateRange = e.detail.date;
    if (dateRange) {
      this.selectedDateRangeForPipeline = dateRange;
    }
  };
  protected onBranchChangedForPipeline = (e: CustomEvent<{ branchId?: string }>) => {
    const branchId = e.detail.branchId;
    if (branchId) {
      this.selectedBranchIdForPipeline = branchId;
      this.selectedUserIdForPipeline = emptyGuid;
    }
  };
  protected onUserChangedForPipeline = (e: CustomEvent<{ userId?: string }>) => {
    if (e.detail.userId) {
      this.selectedUserIdForPipeline = e.detail.userId;
    }
  };
  protected onFilterResetForPipeline = (_: CustomEvent<NonNullable<unknown>>) => {
    if (this.availableDateRanges.length > 1) {
      this.selectedDateRangeForPipeline = this.availableDateRanges[1];
    }
    this.selectedBranchIdForPipeline = this.availableBranches.length === 1 ? this.availableBranches[0].id : emptyGuid;
    this.selectedUserIdForPipeline = emptyGuid;
  };
}
